<template>
    <div class="report-details">
        <!-- Header with editable name -->
        <div class="report-header">
            <div class="report-name-wrapper" v-if="!editingName" @click="startEditName">
                <h1 class="report-name">{{ report.name }}</h1>
                <span class="edit-icon" v-if="isOwner">&#9998;</span>
            </div>
            <div class="report-name-edit" v-if="editingName">
                <input
                    type="text"
                    class="form-control"
                    v-model="editName"
                    ref="nameInput"
                    @blur="saveName"
                    @keydown.enter="saveName"
                    @keydown.esc="cancelEditName"
                >
            </div>
            <p class="report-meta">
                <span class="badge badge-field">{{ result.summary.field_name }}</span>
            </p>
        </div>

        <!-- Summary -->
        <div class="summary-info mb-4">
            <p><strong>Submissions Analyzed:</strong> {{ result.summary.filtered_submissions }}</p>
            <p><strong>Unique Values:</strong> {{ result.summary.unique_values }}</p>
            <p><strong>Period:</strong> {{ result.summary.period_label }}</p>
        </div>

        <!-- Bar Chart - Value Distribution -->
        <div class="chart-section mb-4" v-if="result.breakdown.length > 0">
            <h2 class="section-title">Value Distribution</h2>
            <VueApexCharts type="bar" height="280" :options="barChartOptions" :series="barSeries"></VueApexCharts>
        </div>

        <!-- Line Chart - Trends -->
        <div class="chart-section mb-4" v-if="result.trends.labels.length > 1">
            <h2 class="section-title">Trends Over Time</h2>
            <VueApexCharts type="line" height="280" :options="lineChartOptions" :series="lineSeries"></VueApexCharts>
        </div>

        <!-- Breakdown Table -->
        <div class="breakdown-section mb-4" v-if="result.breakdown.length > 0">
            <h2 class="section-title">Breakdown Table</h2>
            <table class="table breakdown-table">
                <thead>
                    <tr>
                        <th>Value</th>
                        <th>Count</th>
                        <th>Percentage</th>
                        <th>Distribution</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(item, idx) in result.breakdown" :key="idx">
                        <td>{{ item.value }}</td>
                        <td>{{ item.count }}</td>
                        <td>{{ item.percentage }}%</td>
                        <td>
                            <div class="bar-wrapper">
                                <div class="bar-fill" :style="{ width: item.percentage + '%' }"></div>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- Share Link -->
        <div class="share-link-section" v-if="report.share_token">
            <h2 class="section-title">Share Report</h2>
            <CopyField :content="shareUrl" button="Copy Link"></CopyField>
        </div>

        <!-- Actions -->
        <div class="report-actions">
            <button class="btn btn-primary mr-2" @click="exportPDF" :disabled="exporting">
                <span class="spinner-border" v-if="exporting"></span>
                <span :class="{'opacity-0': exporting}"><img src="@/assets/icons/download.svg" alt="Export"> Export PDF</span>
            </button>
            <button class="btn btn-delete ml-auto" v-if="isOwner" @click="confirmDelete">
                <img src="@/assets/icons/trash.svg" alt="Delete"> Delete Report
            </button>
        </div>
    </div>
</template>

<script>
import repository from "@/repository/repository";
import VueApexCharts from "vue-apexcharts";
import CopyField from "@/components/widgets/CopyField";

const CHART_COLORS = [
    '#031FEB', '#FF331F', '#22C55E', '#F59E0B', '#8B5CF6',
    '#EC4899', '#06B6D4', '#F97316', '#6366F1', '#14B8A6',
    '#EF4444', '#84CC16', '#A855F7', '#3B82F6', '#10B981',
    '#E11D48', '#0EA5E9', '#D946EF', '#F43F5E', '#64748B',
];

export default {
    name: "ReportDetailsModal",
    components: { VueApexCharts, CopyField },
    props: ['report', 'formName'],
    data() {
        return {
            editingName: false,
            editName: '',
            exporting: false,
        }
    },
    methods: {
        startEditName() {
            if (!this.isOwner) return;
            this.editingName = true;
            this.editName = this.report.name;
            this.$nextTick(() => {
                this.$refs.nameInput.focus();
                this.$refs.nameInput.select();
            });
        },
        saveName() {
            if (!this.editName.trim() || this.editName.trim() === this.report.name) {
                this.editingName = false;
                return;
            }
            repository.put("/reports/" + this.report.hashId, { name: this.editName.trim() })
                .then(response => {
                    this.$emit('renamed', response.data.report.name);
                    this.editingName = false;
                })
                .catch(() => {
                    this.editingName = false;
                })
        },
        cancelEditName() {
            this.editingName = false;
        },
        exportPDF() {
            if (this.exporting) return;
            this.exporting = true;

            repository.get("/reports/" + this.report.hashId + "/pdf", {
                responseType: 'blob',
                timeout: 30000,
            })
                .then(response => {
                    const blob = new Blob([response.data], { type: 'application/pdf' });
                    const link = document.createElement('a');
                    link.href = URL.createObjectURL(blob);
                    link.download = this.$store.getters.currentProject.name + ' - ' + this.formName + ' - ' + this.report.name + ' Report.pdf';
                    link.click();
                    URL.revokeObjectURL(link.href);
                    this.exporting = false;
                })
                .catch(() => {
                    this.exporting = false;
                })
        },
        confirmDelete() {
            this.$confirm({
                title: "Delete report?",
                message: "Are you sure you want to delete this report? This action cannot be undone.",
                button: {
                    yes: "Yes, delete",
                    no: "No, cancel"
                },
                callback: confirm => {
                    if (confirm) {
                        repository.delete("/reports/" + this.report.hashId)
                            .then(() => {
                                this.$emit('deleted');
                                this.$emit('close');
                            })
                    }
                }
            });
        },
    },
    computed: {
        isOwner() {
            return this.$store.getters.isOwner;
        },
        shareUrl() {
            return window.location.origin + '/report/' + this.report.share_token;
        },
        result() {
            return this.report.result;
        },
        barSeries() {
            return [{
                name: 'Count',
                data: this.result.breakdown.map(item => item.count),
            }];
        },
        barChartOptions() {
            return {
                chart: {
                    type: 'bar',
                    toolbar: { show: false },
                },
                plotOptions: {
                    bar: {
                        columnWidth: '60%',
                        borderRadius: 6,
                        distributed: true,
                    },
                },
                colors: CHART_COLORS.slice(0, this.result.breakdown.length),
                dataLabels: { enabled: false },
                legend: { show: false },
                xaxis: {
                    categories: this.result.breakdown.map(item => item.value),
                    labels: {
                        style: { fontSize: '11px' },
                        trim: true,
                        maxHeight: 80,
                    },
                },
                yaxis: {
                    labels: {
                        formatter: (val) => Math.round(val),
                    },
                },
                grid: { borderColor: '#eee' },
                tooltip: {
                    y: {
                        formatter: (val, { dataPointIndex }) => {
                            const item = this.result.breakdown[dataPointIndex];
                            return val + ' (' + item.percentage + '%)';
                        },
                    },
                },
            };
        },
        lineSeries() {
            return this.result.trends.datasets.map((ds, idx) => ({
                name: ds.value,
                data: ds.data,
                color: CHART_COLORS[idx % CHART_COLORS.length],
            }));
        },
        lineChartOptions() {
            return {
                chart: {
                    type: 'line',
                    toolbar: { show: false },
                },
                stroke: {
                    curve: 'smooth',
                    width: 2,
                },
                xaxis: {
                    categories: this.result.trends.labels,
                    labels: {
                        formatter: (val) => {
                            if (!val) return '';
                            const parts = val.split('-');
                            if (parts.length === 2) {
                                const date = new Date(parts[0], parts[1] - 1);
                                return date.toLocaleString('default', { month: 'short', year: '2-digit' });
                            }
                            return val;
                        },
                    },
                },
                yaxis: {
                    labels: {
                        formatter: (val) => Math.round(val),
                    },
                },
                dataLabels: { enabled: false },
                grid: { borderColor: '#eee' },
                legend: {
                    position: 'bottom',
                    fontSize: '12px',
                },
            };
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../scss/variables";

.report-details {
    max-height: 75vh;
    overflow-y: auto;
    margin: -2.5rem;
    padding: 2.5rem;

    @include smartphone {
        margin: -20px;
        padding: 20px;
    }
}

.report-header {
    margin-bottom: 1.5rem;

    .report-name-wrapper {
        display: flex;
        align-items: center;
        cursor: pointer;

        &:hover .edit-icon {
            opacity: 1;
        }
    }

    .report-name {
        font-size: 1.35rem;
        font-weight: 700;
        color: $dark;
        margin: 0;
    }

    .edit-icon {
        margin-left: 8px;
        opacity: 0;
        color: $grey-text;
        font-size: 1rem;
        @extend .animated;
    }

    .report-name-edit input {
        font-size: 1.35rem;
        font-weight: 700;
        padding: 0.25rem 0.5rem;
        margin-bottom: 0.5rem;
    }

    .report-meta {
        margin-top: 1.5rem;
        font-size: 0.9rem;
    }

    .badge-field {
        background: rgba($primary, 0.1);
        color: $primary;
        font-weight: 600;
        padding: 3px 8px;
        border-radius: 4px;
        font-size: 0.8rem;
    }
}

.summary-info {
    p {
        margin-bottom: 0.25rem;
        font-size: 0.9rem;
        color: $grey-text;

        strong {
            color: $dark;
            font-weight: 600;
        }
    }
}

.section-title {
    font-size: 1rem;
    font-weight: 600;
    color: $dark;
    margin-bottom: 0.75rem;
}

.breakdown-table {
    font-size: 0.85rem;

    th {
        font-weight: 600;
        color: $grey-text;
        border-top: none;
        padding: 0.5rem;
    }

    td {
        padding: 0.5rem;
        vertical-align: middle;
    }

    .bar-wrapper {
        background: $hover-grey;
        border-radius: 10px;
        height: 16px;
        overflow: visible;

        .bar-fill {
            background: $primary;
            height: 100%;
            border-radius: 10px;
            min-width: 2px;
            box-shadow: 0 2px 6px rgba($primary, 0.4);
        }
    }
}

.share-link-section {
    border-top: 1px solid $border-grey;
    padding-top: 1rem;
    margin-top: 1rem;
}

.report-actions {
    padding-top: 1rem;
    display: flex;
    align-items: center;

    .btn img {
        width: 1rem;
        height: 1rem;
        margin-right: 5px;
        filter: brightness(0) invert(1);
        vertical-align: -1px;
    }

    .btn-delete {
        background: rgba($danger, 0.1);
        border-color: transparent;
        font-weight: 600;
        color: $danger;
        padding: 10px 20px;
        height: auto;
        border-radius: $box-border-radius;
        display: inline-flex;
        align-items: center;
        justify-content: center;

        img {
            filter: brightness(0) saturate(100%) invert(27%) sepia(91%) saturate(5038%) hue-rotate(355deg) brightness(101%) contrast(107%);
        }

        &:hover {
            background: $danger;
            border-color: $danger;
            color: $white;

            img {
                filter: brightness(0) invert(1);
            }
        }

        &:active {
            background: darken($danger, 7.5%) !important;
            border-color: darken($danger, 7.5%) !important;
            color: $white;
            box-shadow: none !important;
        }
    }
}
</style>
